import { useAxios } from "../useAxios";
import { useStripboardList } from "./useStripboardList";
import { useStripboardMutations } from "./useStripboardMutations";
import { useMemo } from "react";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { checkProjectFeatureStatus, instanceOfStripboardTableDayBreak } from "utils";
import { useProjectsApi } from "../useProjects";
import { StripboardTableDayBreak } from "components/dashboard/body/projects/panels/schedule/types";

export interface UseStripboardApiResult {
  // Query results
  stripboardData: ReturnType<typeof useStripboardList>["stripboardData"];
  boneyardData: ReturnType<typeof useStripboardList>["boneyardData"];
  isStripboardLoading: boolean;
  isBoneyardLoading: boolean;
  isStripboardError: boolean;
  isBoneyardError: boolean;
  stripboardError: Error | null;
  boneyardError: Error | null;
  refetchStripboard: () => Promise<any>;
  refetchBoneyard: () => Promise<any>;

  // Mutations
  updateDayBreakIndex: ReturnType<typeof useStripboardMutations>["updateDayBreakIndex"];
  updateBannerIndex: ReturnType<typeof useStripboardMutations>["updateBannerIndex"];
  updateDayBreakDate: ReturnType<typeof useStripboardMutations>["updateDayBreakDate"];
  createManualDaybreak: ReturnType<typeof useStripboardMutations>["createManualDaybreak"];
  createBanner: ReturnType<typeof useStripboardMutations>["createBanner"];
  updateDayBreakLock: ReturnType<typeof useStripboardMutations>["updateDayBreakLock"];
  updateDaybreakLockDebounce: ReturnType<
    typeof useStripboardMutations
  >["updateDaybreakLockDebounce"];
  moveStripboardToBoneyard: ReturnType<typeof useStripboardMutations>["moveStripboardToBoneyard"];
  moveBoneyardToStripBoard: ReturnType<typeof useStripboardMutations>["moveBoneyardToStripBoard"];
  deleteDayBreak: ReturnType<typeof useStripboardMutations>["deleteDayBreak"];
  deleteBanner: ReturnType<typeof useStripboardMutations>["deleteBanner"];
  duplicateStripboard: ReturnType<typeof useStripboardMutations>["duplicateStripboard"];
  updateStripboard: ReturnType<typeof useStripboardMutations>["updateStripboard"];
  reRunScheduling: ReturnType<typeof useStripboardMutations>["reRunScheduling"];
  createAutomaticDayBreaks: ReturnType<typeof useStripboardMutations>["createAutomaticDayBreaks"];

  // Derived data
  daybreaks: StripboardTableDayBreak[];
}

export const useStripboardApi = (
  isBoneyard: boolean = false,
  enabled: boolean = false
): UseStripboardApiResult => {
  const axios = useAxios({ allResponse: false });
  const { projectId, documentId, hasValidIds, project } = useProjectIdentifiers();
  const { useProject } = useProjectsApi({});
  const { data: fetchedProject } = useProject(
    enabled && hasValidIds && !project?.featureStatuses ? projectId : undefined
  );

  const currentProject = project || fetchedProject;

  const scheduleStatus = useMemo(() => {
    if (!enabled || !hasValidIds || !currentProject?.featureStatuses) return false;
    return checkProjectFeatureStatus(
      currentProject.featureStatuses.map((fs: any) => ({
        feature_status_id: fs.featureStatusId,
        project_id: String(currentProject?.projectId ?? projectId ?? ""),
        feature: fs.feature,
        status: fs.status,
        metadata: fs.metadata,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })),
      "schedule"
    );
  }, [enabled, hasValidIds, currentProject?.featureStatuses, currentProject?.projectId, projectId]);

  const listResult = useStripboardList({
    projectId: projectId || 0,
    documentId: documentId || 0,
    axios,
    hasValidIds,
    isBoneyard,
    scheduleStatus,
    enabled,
  });

  const mutations = useStripboardMutations({
    projectId: projectId || 0,
    documentId: documentId || 0,
  });

  const daybreaks = useMemo(() => {
    const data = isBoneyard ? listResult.boneyardData : listResult.stripboardData;
    if (!data) return [];
    return (data as any[]).filter((item: any) =>
      instanceOfStripboardTableDayBreak(item)
    ) as StripboardTableDayBreak[];
  }, [isBoneyard, listResult.boneyardData, listResult.stripboardData]);

  return useMemo(
    () => ({
      ...listResult,
      ...mutations,
      daybreaks,
    }),
    [listResult, mutations, daybreaks]
  );
};

export * from "./useStripboard.types";
export * from "./useStripboardActions";
export * from "./useStripboardColumns";
export { useStripboardMutations } from "./useStripboardMutations";
